import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBell,
  FaUser,
  FaLock,
  FaSignOutAlt,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaRedo,
} from "react-icons/fa";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../../Styles/AminNav.css";
import profilePic from "../../assets/board1.jpg";

import ApiService from "../../nhrc_apis/ApiService";

const { PasswordAPI } = ApiService;

function AdminNav() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const [adminName, setAdminName] = useState("ADMIN");
  const [user, setUser] = useState(null);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  /* =========================================================
     CHANGE PASSWORD MODAL STATES

     Step 1: email -> PasswordAPI.forgotPassword(email)
             (backend emails an OTP)
     Step 2: OTP + new password + confirm password ->
             PasswordAPI.resetPassword({ email, otp,
             new_password, confirm_password }) in one call.
     Email is pre-filled from the logged-in admin (editable).
  ========================================================= */

  const [showChangePwModal, setShowChangePwModal] = useState(false);
  const [changePwStep, setChangePwStep] = useState(1);

  const [changePwEmail, setChangePwEmail] = useState("");
  const [sendingChangePwOtp, setSendingChangePwOtp] = useState(false);

  const [changePwOtp, setChangePwOtp] = useState("");
  const [changePwNewPassword, setChangePwNewPassword] = useState("");
  const [changePwConfirmPassword, setChangePwConfirmPassword] = useState("");
  const [showChangePwNewPassword, setShowChangePwNewPassword] = useState(false);
  const [resettingChangePw, setResettingChangePw] = useState(false);

  /* ================= GET USER FROM STORAGE ================= */
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (!storedUser) return;

      const parsedUser = JSON.parse(storedUser);

      setUser(parsedUser);

      // SAFE EXTRACTION (handles all backend formats)
      const displayName =
        parsedUser?.name ||
        parsedUser?.full_name ||
        parsedUser?.fullName ||
        parsedUser?.username ||
        parsedUser?.email ||
        "ADMIN";

      setAdminName(displayName);
    } catch (err) {
      console.log("User parse error:", err);
      setAdminName("ADMIN");
    }
  }, []);

  /* ================= CLOSE DROPDOWN ================= */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    navigate("/", { replace: true });
    setDropdownOpen(false);
  };

  /* =========================================================
     CHANGE PASSWORD HANDLERS
  ========================================================= */

  const openChangePwModal = () => {
    setShowChangePwModal(true);
    setChangePwStep(1);
    setChangePwEmail(user?.email || "");
    setChangePwOtp("");
    setChangePwNewPassword("");
    setChangePwConfirmPassword("");
    setShowChangePwNewPassword(false);
  };

  const closeChangePwModal = () => {
    setShowChangePwModal(false);
  };

  /* STEP 1 — send OTP */
  const handleSendChangePwOtp = async () => {
    if (!changePwEmail.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    try {
      setSendingChangePwOtp(true);

      await PasswordAPI.forgotPassword(
        changePwEmail.trim().toLowerCase()
      );

      toast.success("OTP sent to your email");

      setChangePwStep(2);
    } catch (err) {
      console.error("CHANGE PASSWORD - SEND OTP ERROR:", err);

      toast.error(
        err?.response?.data?.detail ||
          "Failed to send OTP. Please check the email and try again."
      );
    } finally {
      setSendingChangePwOtp(false);
    }
  };

  /* STEP 2 — verify OTP + set new password in one call */
  const handleChangePassword = async () => {
    if (!changePwOtp.trim()) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (!changePwNewPassword.trim() || !changePwConfirmPassword.trim()) {
      toast.error("Please fill both password fields.");
      return;
    }

    if (changePwNewPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (changePwNewPassword !== changePwConfirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setResettingChangePw(true);

      await PasswordAPI.resetPassword({
        email: changePwEmail.trim().toLowerCase(),
        otp: changePwOtp.trim(),
        new_password: changePwNewPassword.trim(),
        confirm_password: changePwConfirmPassword.trim(),
      });

      toast.success("Password changed successfully!");

      closeChangePwModal();
    } catch (err) {
      console.error("CHANGE PASSWORD - RESET ERROR:", err);

      toast.error(
        err?.response?.data?.detail || "Failed to change password"
      );
    } finally {
      setResettingChangePw(false);
    }
  };

  return (
    <nav className="adminnav-container">

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      {/* LEFT */}
      <div className="adminnav-left">
        <Link to="/dashboard" className="adminnav-title-link">
          <span className="adminnav-title">
            ADMIN: {adminName}
          </span>
        </Link>
      </div>

      {/* CENTER */}
      <div className="adminnav-center">
        <span className="adminnav-center-text">
          National Human Resource Club
        </span>
      </div>

      {/* RIGHT */}
      <div className="adminnav-right">
        <FaBell className="adminnav-right-icon" />

        <div className="adminnav-profile" ref={dropdownRef}>
          <img
            src={profilePic}
            alt="Profile"
            className="adminnav-profile-img"
            onClick={toggleDropdown}
          />

          {dropdownOpen && (
            <div className="adminnav-dropdown">

              <button
                className="adminnav-dropdown-item"
                onClick={() => {
                  navigate("/dashboard/CandidateProfile");
                  setDropdownOpen(false);
                }}
              >
                <FaUser className="adminnav-dropdown-icon" />
                My Profile
              </button>

              <button
                className="adminnav-dropdown-item"
                onClick={() => {
                  openChangePwModal();
                  setDropdownOpen(false);
                }}
              >
                <FaLock className="adminnav-dropdown-icon" />
                Change Password
              </button>

              <button
                className="adminnav-dropdown-item adminnav-logout"
                onClick={handleLogout}
              >
                <FaSignOutAlt className="adminnav-dropdown-icon" />
                Log Out
              </button>

            </div>
          )}
        </div>
      </div>

      {/* =========================================================
          CHANGE PASSWORD MODAL
      ========================================================= */}

      {showChangePwModal && (
        <div
          className="adminchangepw-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div
            className="adminchangepw-modal-card"
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px",
              width: "100%",
              maxWidth: "400px",
              position: "relative",
              color: "#333",
            }}
          >
            <button
              type="button"
              className="adminchangepw-modal-close"
              onClick={closeChangePwModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h4 style={{ marginBottom: "8px" }}>Change Password</h4>

            {/* ============ STEP 1: EMAIL ============ */}
            {changePwStep === 1 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Confirm your email address and we'll send you an OTP.
                </p>

                <input
                  type="email"
                  placeholder="Enter your email"
                  value={changePwEmail}
                  onChange={(e) => setChangePwEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                  }}
                />

                <button
                  type="button"
                  disabled={sendingChangePwOtp}
                  onClick={handleSendChangePwOtp}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#4b3df5",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {sendingChangePwOtp ? "Sending OTP..." : "Send OTP"}
                </button>
              </>
            )}

            {/* ============ STEP 2: OTP + NEW PASSWORD ============ */}
            {changePwStep === 2 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Enter the OTP sent to <b>{changePwEmail}</b> and your new password.
                </p>

                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={changePwOtp}
                  onChange={(e) => setChangePwOtp(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                  }}
                />

                <div style={{ position: "relative", marginBottom: "12px" }}>
                  <input
                    type={showChangePwNewPassword ? "text" : "password"}
                    placeholder="New password"
                    value={changePwNewPassword}
                    onChange={(e) => setChangePwNewPassword(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 40px 10px 12px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowChangePwNewPassword(!showChangePwNewPassword)}
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "10px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    {showChangePwNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <input
                  type={showChangePwNewPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={changePwConfirmPassword}
                  onChange={(e) => setChangePwConfirmPassword(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    marginBottom: "16px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                  }}
                />

                <button
                  type="button"
                  disabled={resettingChangePw}
                  onClick={handleChangePassword}
                  style={{
                    width: "100%",
                    padding: "10px",
                    marginBottom: "8px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#4b3df5",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {resettingChangePw ? "Changing..." : "Change Password"}
                </button>

                <button
                  type="button"
                  disabled={sendingChangePwOtp}
                  onClick={handleSendChangePwOtp}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#333",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <FaRedo />
                  Resend OTP
                </button>
              </>
            )}

          </div>
        </div>
      )}

    </nav>
  );
}

export default AdminNav;